<script lang="ts">
  import { api, ApiError } from '../../lib/api';
  import { persistSession } from '../../stores/auth';
  import { firebaseAuth } from '../../lib/firebase';
  import { createUserWithEmailAndPassword } from 'firebase/auth';

  type Props = { onSuccess?: () => void };
  let { onSuccess }: Props = $props();

  let email = $state('');
  let nickname = $state('');
  let cellphone = $state('');
  let password = $state('');
  let loading = $state(false);
  let errorMsg = $state('');

  type Me = { id: string; nickname: string; avatar?: string; balance_cents?: number };

  function friendlyFirebaseError(code: string): string {
    switch (code) {
      case 'auth/invalid-email':
        return 'Email invalido.';
      case 'auth/email-already-in-use':
        return 'Esse email ja esta cadastrado.';
      case 'auth/weak-password':
        return 'Senha muito fraca (minimo 6 caracteres).';
      case 'auth/network-request-failed':
        return 'Sem conexao. Verifica sua rede.';
      default:
        return `Erro: ${code}`;
    }
  }

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (loading) return;
    errorMsg = '';
    loading = true;
    try {
      // 1. Cria conta no Firebase Auth (cliente-side). Email + senha
      //    vivem so no Firebase, backend nao recebe a senha.
      await createUserWithEmailAndPassword(firebaseAuth, email, password);
      // 2. onIdTokenChanged em stores/auth.ts ja capturou o ID token.
      //    Chama POST /users/profile com nickname etc — backend cria o
      //    UserProfile no Firestore com UID = Firebase UID.
      const me = await api.post<Me>('/users/profile', {
        nickname,
        cellphone,
      });
      persistSession('', me.id, me.nickname, me.avatar);
      onSuccess?.();
    } catch (err: unknown) {
      const fbErr = err as { code?: string };
      if (fbErr && typeof fbErr.code === 'string' && fbErr.code.startsWith('auth/')) {
        errorMsg = friendlyFirebaseError(fbErr.code);
      } else if (err instanceof ApiError) {
        errorMsg = err.message || `Erro ${err.status}`;
      } else {
        errorMsg = String(err);
      }
    } finally {
      loading = false;
    }
  }
</script>

<form onsubmit={handleSubmit}>
  <label>
    <span>email</span>
    <input type="email" bind:value={email} autocomplete="email" required disabled={loading} />
  </label>

  <label>
    <span>nickname (opcional)</span>
    <input type="text" bind:value={nickname} autocomplete="nickname" disabled={loading} maxlength="32" placeholder="deixa em branco pra gente escolher um" />
  </label>

  <label>
    <span>celular</span>
    <input type="tel" bind:value={cellphone} autocomplete="tel" required disabled={loading} maxlength="20" />
  </label>

  <label>
    <span>senha</span>
    <input type="password" bind:value={password} autocomplete="new-password" required disabled={loading} minlength="6" />
  </label>

  {#if errorMsg}
    <div class="error" role="alert">{errorMsg}</div>
  {/if}

  <button
    type="submit"
    disabled={loading || !email || !nickname || !cellphone || !password}
    class="primary"
  >
    {loading ? 'criando...' : 'criar conta'}
  </button>
</form>

<style>
  form { display: flex; flex-direction: column; gap: 14px; }
  label { display: flex; flex-direction: column; gap: 5px; }
  label span {
    font-size: 11px;
    color: #0D47A1;
    font-weight: 800;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  form input {
    width: 100%;
    box-sizing: border-box;
    padding: 11px 12px;
    font-size: 15px;
    color: #0D47A1;
    background: #fff;
    border: 2px solid #90caf9;
    border-radius: 8px;
    outline: none;
    transition: border-color 0.12s, box-shadow 0.12s;
  }
  form input::placeholder { color: #90a4ae; }
  form input:focus {
    border-color: #1565C0;
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.18);
  }
  form input:disabled { opacity: 0.6; }
  .error {
    color: #c62828;
    background: rgba(198, 40, 40, 0.08);
    border: 1.5px solid rgba(198, 40, 40, 0.35);
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 600;
  }
  .primary {
    background: linear-gradient(180deg, #66bb6a, #43a047 55%, #2e7d32);
    color: #fff;
    border: 2px solid #1b5e20;
    border-radius: 10px;
    font-weight: 900;
    letter-spacing: 1px;
    text-transform: uppercase;
    padding: 12px;
    margin-top: 4px;
    cursor: pointer;
    box-shadow: 0 3px 0 #1b5e20, inset 0 1px 0 rgba(255, 255, 255, 0.3);
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
    transition: transform 0.08s, box-shadow 0.08s;
  }
  .primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 0 #1b5e20, inset 0 1px 0 rgba(255,255,255,0.3); }
  .primary:active:not(:disabled) { transform: translateY(2px); box-shadow: 0 1px 0 #1b5e20; }
  .primary:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
